import { Box, Button, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  useResumeAfterVenueMaintenance,
  useVenueMaintenanceState
} from '../../api/useEntry';
import { HEADER_CONTROL_BTN_SX } from './headerButtonSx';

const MAINTENANCE_TRIGGER = 'VENUE_MAINTENANCE';
/** The backend's answer when it could not READ the key — not someone's halt. */
const UNREADABLE = 'unknown';

/**
 * The operator's view of a venue maintenance halt, and the only button that
 * lifts it.
 *
 * The watcher currently ships OBSERVE-ONLY, so the common case here is a window
 * with NOTHING halted: the chip says so and there is no button, because the
 * response is the operator's to choose. The halted copy below is what the same
 * component renders once `VENUE_MAINTENANCE_ENFORCE` is on.
 *
 * The watcher halts BOTH engines ahead of an announced Kalshi/Polymarket
 * maintenance window and — deliberately — never resumes trading on its own. So
 * there has to be a control here: the only other thing that clears the halt key
 * is global START OPENER, which the UI greys out whenever global state already
 * reads "Running". That is the 2026-08-06 deadlock, and a scheduled window
 * would walk into it every single time.
 *
 * EVERYTHING it renders is derived from the polled backend state — never from
 * the resume mutation's own result. This component is mounted on two SEPARATE
 * routes (the opener page and the unwinder page), and the backend drops its
 * held window on every resume, so a verdict cached in the browser disappears
 * the moment the operator navigates: they would resume from the opener page,
 * see "still armed", switch to the unwinder page to check the other direction,
 * and find nothing at all — a false all-clear on the page for the direction
 * most likely still stopped. Reading the halt keys through also means the
 * warning clears itself when someone lifts the other halt, instead of sticking
 * until the next maintenance window.
 */
export const VenueMaintenanceControl = () => {
  const { data } = useVenueMaintenanceState();
  const resume = useResumeAfterVenueMaintenance();

  const held = data?.held ?? null;
  const halts = data?.halts;
  const armFailures = data?.haltArmFailures ?? [];

  const armed = (
    [
      ['opener', halts?.opener],
      ['unwinder', halts?.unwinder]
    ] as const
  ).filter(([, trigger]) => Boolean(trigger));

  // The watcher's observe-only posture: it tracked the window and armed
  // nothing. Rendering the halted copy here would tell the operator the platform
  // is stopped while both engines keep placing into the outage. `undefined` (a
  // backend older than the field) keeps the enforcing reading, which is what it
  // always did.
  const observeOnly = held?.enforced === false;
  // "The watcher did not arm" and "nothing is armed" are DIFFERENT claims, and
  // only the second one may hide the resume. A read that failed (`unknown`) or a
  // key left over from an enforcing run both show up here while `enforced` is
  // false — and hiding the button there recreates the 2026-08-06 deadlock this
  // component exists to break: the engines are stopped, the chips say so, and
  // the only control that lifts them is gone. Pressing it when nothing is ours
  // is harmless — `clearIfTrigger` only ever removes our own trigger.
  const nothingArmed = armed.length === 0;

  // A halt standing on a key that is NOT ours: an operator PANIC or STOP took
  // it over, so pressing resume here will not (and must not) lift it.
  // `unknown` is excluded on purpose — it means the backend could not READ the
  // key, and telling the operator to go clear a stop that does not exist is its
  // own kind of wrong answer.
  const foreign = armed.filter(
    ([, trigger]) => trigger !== MAINTENANCE_TRIGGER && trigger !== UNREADABLE
  );
  const unreadable = armed.filter(([, trigger]) => trigger === UNREADABLE);
  // A maintenance key standing that the headline chip does NOT account for.
  // Two ways to get here and both need naming, because in both the engine is
  // stopped and nothing else on this bar would say so:
  //   - no window in memory — a halt that survived a restart whose adoption
  //     failed, or one orphaned by a race. Nobody refreshes it, nobody else
  //     clears it.
  //   - observe-only — the watcher armed nothing for this window, so the
  //     headline says exactly that, and a key from an earlier enforcing run
  //     would otherwise be invisible: not `foreign` (the trigger IS ours) and
  //     not orphaned (there IS a window). The chip that says "read the ones
  //     beside me" has to have something to point at.
  // Per DIRECTION, not globally: the two keys live on different cluster slots,
  // so one read can fail while the other answers.
  const unaccountedMaintenance =
    held && !observeOnly
      ? []
      : armed.filter(([, t]) => t === MAINTENANCE_TRIGGER);
  // An UNREADABLE key counts for the BUTTON: it may well be ours, and the
  // resume is harmless if it is not.
  const orphaned =
    !held &&
    armed.some(([, t]) => t === MAINTENANCE_TRIGGER || t === UNREADABLE);

  // `isError` is part of the gate: a resume that never reached the manager has
  // to be visible even when the poll says everything is clear, or a failed
  // request looks exactly like a successful one.
  if (
    !held &&
    !orphaned &&
    foreign.length === 0 &&
    unreadable.length === 0 &&
    armFailures.length === 0 &&
    !resume.isError
  ) {
    return null;
  }

  const waiting = Boolean(data?.awaitingOperator);
  // Only meaningful when the VENUE gave us the end. For an unannounced outage
  // the backend invents a placeholder it explicitly refuses to hold to, and
  // printing it would be a fabricated schedule.
  const scheduleKnown = Boolean(held?.endsAtKnown);
  const windowEnded = Boolean(
    held && scheduleKnown && Date.now() >= held.endsAt
  );
  const minutesLeft =
    held && scheduleKnown
      ? Math.max(0, Math.round((held.endsAt - Date.now()) / 60_000))
      : 0;

  const chipLabel = () => {
    if (!held) return '';
    // "NOT halted" is only sayable when nothing is armed. With a key standing
    // (ours from an earlier enforcing run, or an unreadable one) the honest
    // claim is narrower: the WATCHER armed nothing. What IS armed gets its own
    // chip right beside this one.
    if (observeOnly) {
      return nothingArmed
        ? `${held.venue} maintenance — NOT halted`
        : `${held.venue} maintenance — watcher armed nothing`;
    }
    if (waiting) return `${held.venue} maintenance over — halted`;
    if (!scheduleKnown) return `${held.venue} outage — halted, no end announced`;
    if (windowEnded) return `${held.venue} maintenance — halted, venue unconfirmed`;
    return `${held.venue} maintenance — halted${minutesLeft > 0 ? ` ~${minutesLeft}min` : ''}`;
  };

  const chipTitle = () => {
    if (!held) return '';
    if (observeOnly) {
      return nothingArmed
        ? `${held.venue} is in maintenance ("${held.title}") and the watcher is OBSERVE-ONLY: nothing was halted and no order was cancelled, so both engines are still placing into it. Stopping them is your call.`
        : `${held.venue} is in maintenance ("${held.title}") and the watcher armed nothing for it — but a halt key IS standing. Read the chips beside this one for what is actually armed; resume clears it if it is ours.`;
    }
    if (waiting) {
      return `${held.venue} maintenance ("${held.title}") is OVER and the venue reports healthy — the engines are still halted until you resume. Nothing restarts on its own.`;
    }
    if (!scheduleKnown) {
      return `${held.venue} is refusing orders and published no end time, so there is no schedule to show. The engines stay halted until the venue reports healthy again.`;
    }
    if (windowEnded) {
      return `${held.venue} maintenance ("${held.title}") is past its scheduled end, but the venue has not confirmed it is healthy again. Resuming now is your call.`;
    }
    return `${held.venue} maintenance in progress ("${held.title}") until ${new Date(held.endsAt).toLocaleTimeString()}.`;
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {held && (
          <Tooltip title={chipTitle()} placement="top">
            <Chip
              size="small"
              icon={<BuildCircleIcon sx={{ fontSize: 14 }} />}
              color={waiting || observeOnly ? 'warning' : 'error'}
              variant={waiting || observeOnly ? 'outlined' : 'filled'}
              label={chipLabel()}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
        {unreadable.map(([direction]) => (
          <Tooltip
            key={`unreadable-${direction}`}
            title={`The ${direction} halt key could not be READ, so its state is UNKNOWN — assume the ${direction} is stopped until this clears. It is not evidence that anyone halted it.`}
            placement="top"
          >
            <Chip
              size="small"
              color="warning"
              label={`${direction} halt state UNREADABLE`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {unaccountedMaintenance.map(([direction]) => (
          <Tooltip
            key={`orphan-${direction}`}
            title={
              held
                ? `A maintenance halt is armed on the ${direction} even though the watcher armed nothing for this window — most likely it is left over from before the kill switch was flipped. That engine is STOPPED. Resume lifts it.`
                : `A maintenance halt is still armed on the ${direction} but the watcher has no window for it — most likely it outlived a restart. That engine is STOPPED. Resume lifts it.`
            }
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              icon={<BuildCircleIcon sx={{ fontSize: 14 }} />}
              label={
                held
                  ? `${direction} halt armed — maintenance`
                  : `${direction} halt armed — no window tracked`
              }
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {((held && !(observeOnly && nothingArmed)) || orphaned) && (
          // Offered even mid-window: the operator overrides the watcher, not the
          // other way round. The tooltip says plainly what they are overriding.
          // NOT offered for an observe-only hold: there is no halt to lift, and
          // a button that claims to resume trading which is already running is
          // how an operator ends up believing the platform was ever stopped.
          <Tooltip
            title={
              orphaned
                ? 'Lift the orphaned maintenance halt so both engines can trade again.'
                : observeOnly
                  ? 'The watcher halted nothing for this window, but something IS armed on a halt key. This clears it if it is the maintenance watcher\'s.'
                  : waiting
                    ? 'Lift the maintenance halt and let both engines trade again.'
                    : 'The venue has NOT confirmed it is trading again — resuming now can put the makers back against a venue still refusing orders.'
            }
            placement="top"
          >
            <span>
              <Button
                variant={waiting || orphaned ? 'contained' : 'outlined'}
                color={waiting || orphaned ? 'success' : 'warning'}
                size="small"
                startIcon={
                  resume.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => resume.mutate()}
                disabled={resume.isLoading}
                sx={HEADER_CONTROL_BTN_SX}
              >
                {waiting || orphaned ? 'Resume trading' : 'Resume anyway'}
              </Button>
            </span>
          </Tooltip>
        )}
        {/* A halt on a key that is not ours. Named by its actual trigger and by
            direction: "PANIC" sends the operator to the panic lockout, "STOP"
            does not, and one direction being held while the other trades is a
            different situation from both being down. */}
        {foreign.map(([direction, trigger]) => (
          <Tooltip
            key={direction}
            title={`The ${direction} halt key is held by ${trigger}, not by the maintenance watcher — resuming here will NOT lift it. Clear that ${trigger === 'PANIC' ? 'panic' : 'stop'} to let the ${direction} trade again.`}
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              label={`${direction} halted by ${trigger}`}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        ))}
        {/* The loudest thing this control can say: a direction the watcher could
            NOT halt may still be placing INTO the window. */}
        {armFailures.length > 0 && (
          <Tooltip
            title={`The halt could not be armed on: ${armFailures.join('; ')}. That engine may still be placing into the maintenance window — check it by hand.`}
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              label="halt NOT armed on one direction"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
        {resume.isError && (
          <Tooltip
            // Deliberately does NOT claim the halt is still armed: the resume
            // clears the keys BEFORE the steps that can throw, so a 500 here is
            // consistent with the engines being live. The chips above say what
            // is actually armed; this one only says the request failed.
            title="The resume request failed. Check the halt chips above for what is actually armed before assuming anything changed."
            placement="top"
          >
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label="resume request FAILED"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
